import React, {useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle} from 'react';
import {useDispatch} from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {labels, currentResource} from '../../../../../../../util/utils';
import {useEngagementId} from '../../../../../../../util/customHooks';
import {getConfigurationForEntity} from './relateEntities.utils';
import RelateItemList from '../../../../../RelateList/RelateItemList';
import Modal from '../../../../../Modal/Modal';
import RelateEntitiesItem from './RelateEntitiesItem';
import RelateEntitiesHeader from './RelateEntitiesHeader';
import {setIsModalOpen} from '../../../../../../../actions/setismodalopen';
import {clientSideEventNames} from '../../../../../../../util/uiconstants';

const RelateEntitiesContainer = forwardRef((props, ref) => {
	const {bodyId, entityId, parentEntity, selectedItemIds} = props;

	const dispatch = useDispatch();
	const engagementId = useEngagementId();

	const selectedIds = useRef(selectedItemIds);
	const [showSearch, setShowSearch] = useState(true);
	const [showPagination, setShowPagination] = useState(true);

	const [page, setPage] = useState(1);

	useEffect(() => {
		selectedIds.current = selectedItemIds || [];
	}, [selectedItemIds]);

	const [showModal, setShowModal] = useState(false);

	useImperativeHandle(ref, () => {
		return {
			toggleModal() {
				setShowModal(!showModal);
				dispatch(setIsModalOpen(!showModal));
			}
		};
	});

	// Get configuration for my entityId:
	const relateConfig = getConfigurationForEntity(entityId, props);

	const renderItem = React.useCallback(
		(item) => {
			if (relateConfig && relateConfig.render) {
				return relateConfig.render(item);
			} else {
				return <RelateEntitiesItem item={item} />;
			}
		},
		[relateConfig]
	);

	const componentHeader = useCallback(() => {
		const _headerComponent =
			relateConfig && relateConfig.componentHeader ? relateConfig.componentHeader : <RelateEntitiesHeader />;

		return React.cloneElement(_headerComponent, {
			title: relateConfig.title,
			instructionalText: relateConfig.description,
			entityLabel: relateConfig.parentEntityLabel,
			entityName: relateConfig.parentEntityName(parentEntity)
		});
	}, [relateConfig, parentEntity]);

	const data = useCallback(
		(page, pageSize, searchString, parentItem) => {
			if (!relateConfig.isServerSidePaged) {
				return dispatch(relateConfig.fetchAction(engagementId, false));
			} else {
				return dispatch(relateConfig.fetchAction(engagementId, page, pageSize, searchString, parentItem));
			}
		},
		[relateConfig, dispatch, engagementId]
	);

	const filter = useCallback(
		(element, searchString) => {
			setPage(1);
			return relateConfig.filter(element, searchString);
		},
		[relateConfig]
	);

	const isSelected = React.useCallback(
		(element) => {
			return selectedIds.current?.findIndex((id) => id === element.id) !== -1;
		},
		[selectedItemIds]
	);

	const onCheckChange = useCallback(
		(item, checked) => {
			let onCheckCallback = checked ? relateConfig.onRelateItem : relateConfig.onUnrelateItem;

			return dispatch(onCheckCallback(engagementId, parentEntity.id, item)).then(() => {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			});
		},
		[relateConfig, engagementId, parentEntity, dispatch]
	);

	const onCheckChanged = useCallback((item, checked, hierarchy, response) => {
		// Add or remove item.id from selectedIds array depending on whether I'm checking (checked = true) or unchecking (checked = false) the item
		if (checked) {
			if (!selectedIds.current.find((id) => id === item.id)) {
				selectedIds.current.push(item.id);
			}
		} else {
			selectedIds.current = selectedIds.current.filter((id) => id !== item.id);
		}
	}, []);

	function handleOnHide() {
		if (relateConfig && relateConfig.onModalHide) {
			const result = relateConfig.onModalHide(bodyId);

			if (result?.type) {
				// onModalHide function is an action. Dispatch it
				dispatch(result);
			}
		}
		setPage(1);
		setShowModal(false);
		dispatch(setIsModalOpen(false));
	}

	const configuration = useMemo(() => {
		if (relateConfig) {
			return {
				noResultsLabel: relateConfig.noResultsLabel,
				noDataLabel: relateConfig.noDataLabel,
				pagination: relateConfig.pagination,
				pageSize: relateConfig.pageSize,
				data: data,
				selected: isSelected,
				mapResponse: relateConfig.mapResponse,
				checkBox: relateConfig.checkBox,
				filter: filter,
				render: renderItem,
				onCheckBoxChange: onCheckChange,
				onCheckBoxChanged: onCheckChanged
			};
		}
	}, [relateConfig, data, filter, isSelected, onCheckChange, onCheckChanged, renderItem]);

	const onResolve = (_, state) => {
		const show = state.totalEntityCount > 0;
		setShowSearch(show);
		setShowPagination(show);
	};

	const onPageChanged = useCallback((page) => {
		setPage(page);
	});

	const onPageSizeChanged = useCallback(() => {
		setPage(1);
	});

	return (
		<StyledRelateEntitiesContainer className="StyledRelateEntitiesContainer">
			<Modal
				show={showModal}
				onHide={handleOnHide}
				title={relateConfig?.title}
				closeTitle={relateConfig?.modalCloseTitle || labels.modalCloseTitle}
				modalsize={relateConfig?.modalSize}
				hideConfirmButton
				showButtons={false}
				modalContainerClass="RelateEntitiesContainerModalContainer"
				modalbodystyle="RelateEntitiesContainerModalBody"
			>
				<ComponentHeader>{!showSearch && !showPagination && componentHeader()}</ComponentHeader>
				<StyledRelateItemsList className="StyledRelateItemsList" showSearch={showSearch}>
					{relateConfig && (
						<RelateItemList
							search={showSearch}
							pagination={showPagination}
							searchPlaceholder={relateConfig.searchPlaceholder}
							searchClearHoverText={labels.clearHoverText}
							paginationIconHover={currentResource.pagingSvgHoverText}
							paginationShowText={labels.pagingShowtext}
							listHeaders={showSearch && showPagination ? relateConfig.listHeaders : null}
							componentHeader={componentHeader}
							configuration={configuration}
							showSelectedOnlyText={relateConfig.showSelectedOnlyText}
							onResolve={onResolve}
							page={page}
							onPageChanged={onPageChanged}
							onPageSizeChanged={onPageSizeChanged}
							paginationLabel={labels.pagingShowtext}
						/>
					)}
				</StyledRelateItemsList>
			</Modal>
		</StyledRelateEntitiesContainer>
	);
});

RelateEntitiesContainer.displayName = 'RelateEntitiesContainer';

RelateEntitiesContainer.propTypes = {
	bodyId: PropTypes.number,
	entityId: PropTypes.number,
	parentEntity: PropTypes.object,
	selectedItemIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
};

const ComponentHeader = styled.section`
	padding: var(--px-10) var(--px-20);
	display: flex;
	flex-direction: column;
`;

const StyledRelateEntitiesContainer = styled.section``;

const StyledRelateItemsList = styled.section`
	display: flex;
	height: 100%;
	width: 100%;
	.RelateListContainers {
		.HeaderWrapper {
			padding-bottom: var(--px-24);
		}
		.relate-list-header--generic-entity {
			display: flex;
			flex-flow: row;
			justify-content: space-between;
			.relate-list-header__item-label--generic-entity {
				font-size: var(--px-14);
				font-weight: 400;
				line-height: var(--px-20);
				color: var(--neutrals-900);
				margin-left: var(--px-44);
			}
			.relate-list-header__counter--generic-entity {
				display: flex;
				align-items: center;
				margin-right: var(--px-16);
				margin-left: auto;
				text-align: right;
				font-size: var(--px-12);
				font-weight: 400;
				line-height: var(--px-18);
				color: var(--neutrals-900);
				width: auto;
			}
		}
		.StyledRelateItemsListHeader {
			display: flex;
			justify-content: space-between;
			.RelateItemsListHeaderComponent {
				width: 100%;
			}
			.RelateItemsListHeaderSelectedOnly {
				max-width: var(--px-200);
				.RelateItemsListHeaderSelectedOnlyCheckbox {
					display: block;
				}
				.motif-checkbox {
					.motif-checkbox-label {
						width: calc(100% - var(--px-20));
					}
				}
			}
		}
		.relate-list-toolbar {
			.RelateItemsListSearch {
				.motif-typeahead-close-button {
					top: 0;
					display: flex;
					align-items: center;
				}
			}
		}
		.relate-list-body-wrapper {
			padding: 0 var(--px-10) var(--px-20);
			background-color: var(--neutrals-00white);
			height: 100%;
			.relate-list-no-results {
				font-size: var(--px-14);
				color: var(--neutrals-700);
				display: flex;
				align-items: center;
				height: 100%;
			}
			.RelateItemListItem {
				.relate-item-list-item {
					padding: var(--px-10) 0;
					height: var(--px-40);
					position: relative;
					.relate-item-list-labelTxt {
						line-height: normal;
						font-size: var(--px-14);
						font-weight: 300;
						color: var(--neutrals-900);
					}
					.motif-loader-container,
					.motif-progress-loader-container {
						&.inlineView {
							padding: 0 !important;
							background: transparent !important;
							opacity: 1 !important;
							position: relative !important;
						}
					}
				}
			}
		}
	}
`;

export default RelateEntitiesContainer;
